/**
 * NICU EMR — Offline write queue (IndexedDB) + read cache (localStorage-fallback).
 * Any POST/PATCH/PUT/DELETE that fails due to being offline is queued and re-tried
 * on `window.online` (or manually). GETs are additionally cached to localStorage so the
 * UI has data to render when the network is out (in addition to the service worker cache).
 */

const DB_NAME = "nicu-offline";
const DB_VER = 1;
const STORE = "outbox";

let _dbPromise = null;
function db() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains(STORE)) {
        d.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function tx(mode = "readonly") {
  const d = await db();
  return d.transaction(STORE, mode).objectStore(STORE);
}

export async function enqueue(entry) {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const req = store.add({ ...entry, ts: Date.now() });
    req.onsuccess = () => { notify(); resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

export async function listQueue() {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function removeFromQueue(id) {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => { notify(); resolve(); };
    req.onerror = () => reject(req.error);
  });
}

export async function clearQueue() {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const req = store.clear();
    req.onsuccess = () => { notify(); resolve(); };
    req.onerror = () => reject(req.error);
  });
}

const listeners = new Set();
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function notify() { listeners.forEach((fn) => { try { fn(); } catch (e) { console.warn("Offline queue listener failed", e); } }); }

// --- Read cache in localStorage keyed by URL ---
export function cacheRead(url, data) {
  try { localStorage.setItem("nicu_r_" + url, JSON.stringify({ data, at: Date.now() })); } catch (e) { console.warn("Read cache save failed", e); }
}
export function loadRead(url) {
  try {
    const raw = localStorage.getItem("nicu_r_" + url);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

// --- Flush ---
export async function flushQueue(axiosInstance) {
  const items = await listQueue();
  items.sort((a, b) => a.ts - b.ts);
  let ok = 0, fail = 0;
  for (const it of items) {
    try {
      await axiosInstance.request({
        method: it.method,
        url: it.url,
        data: it.data,
        headers: it.headers || {},
        params: it.params || undefined,
      });
      await removeFromQueue(it.id);
      ok++;
    } catch (e) {
      // If still offline or 5xx, keep it. If 4xx, drop it (bad request).
      const status = e?.response?.status;
      if (status && status >= 400 && status < 500) {
        await removeFromQueue(it.id);
      }
      fail++;
      break; // pause on first failure to preserve ordering
    }
  }
  return { ok, fail, remaining: (await listQueue()).length };
}
